import axios from 'axios';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import router from '@/router';
import { Message } from 'element-ui';

// ===== 工具函数 =====
const getStorage = (key) => {
  try { return JSON.parse(sessionStorage.getItem(key)); } catch { return null; }
};

const getToken = () => getStorage('user-login')?.data?.accessToken || '';
const getRefreshToken = () => getStorage('user-login')?.data?.refreshToken;
const getUserId = () => getStorage('user-login')?.data?.userId;

const setAuth = (accessToken, refreshToken) => {
  const userLogin = getStorage('user-login') || { data: {} };
  if (!userLogin.data) userLogin.data = {};
  userLogin.data.accessToken = accessToken;
  if (refreshToken) userLogin.data.refreshToken = refreshToken;
  sessionStorage.setItem('user-login', JSON.stringify(userLogin));
  sessionStorage.setItem('token', accessToken);
};

// ===== 实例 =====
// 后端 API 地址（生产环境远端）
export const BASE_URL = 'https://pms.yixinwm.com/city';

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
});

// ===== 请求防抖（防重复提交）=====
// 只对"写操作"防抖 — 列表查询（page/list/detail/get/query/search）不防抖
const pendingRequests = new Map();
const READ_ONLY_URL_PATTERNS = [
  /\/page($|\?|\/)/, /\/list($|\?|\/)/, /\/detail($|\?|\/)/,
  /\/get($|\?|\/)/, /\/query($|\?|\/)/, /\/search($|\?|\/)/,
  /\/find($|\?|\/)/, /\/select($|\?|\/)/,
  /\/?login($|\?|\/)/,  // 登录接口不防抖（兼容带/不带前导斜杠）
  /\/pending(-|)tasks($|\?|\/)/,  // 待办任务列表查询
  /\/(groups|options|dict|tree)($|\?|\/)/,  // 下拉选项/树数据
  /\/stats($|\?|\/)/, /\/analysis($|\?|\/)/, /\/screenStats($|\?|\/)/,  // 统计查询
];
function isReadOnlyUrl(url) {
  if (!url) return false;
  return READ_ONLY_URL_PATTERNS.some(p => p.test(url));
}
function getRequestKey(config) {
  const method = (config.method || 'get').toLowerCase();
  if (!['post', 'put', 'patch'].includes(method)) return null;
  // 读操作（分页/列表/查询）不防抖 — 防的是表单重复提交，不是列表刷新
  if (isReadOnlyUrl(config.url)) return null;
  const url = config.url || '';
  let bodyStr = '';
  try {
    bodyStr = JSON.stringify(config.data || {}, (k, v) => {
      if (['_t', '_timestamp', 'requestId'].includes(k)) return undefined;
      return v;
    });
  } catch { bodyStr = ''; }
  return `${method.toUpperCase()} ${url} ${bodyStr}`;
}

// ===== Token 刷新状态 =====
let isRefreshing = false;
let failedQueue = [];
let tokenExpiredToastShown = false;
let isLoggingOut = false; // 防止并发触发多次退出登录

// 所有鉴权失败的业务 code → 退出登录
const AUTH_FAIL_CODES = new Set([
  '600008', 600008, // 请重新登录
  '600009', 600009, // token非法
  '600010', 600010, // 无效的token
  '600011', 600011, // token已过期
  '600012', 600012, // 用户未登录
  '600013', 600013, // refreshToken无效或过期
]);

// ===== 请求拦截器 =====
apiClient.interceptors.request.use(
  (config) => {
    // 跳过刷新接口 — 它有自己的 headers
    if (!config.url?.includes('/token/refresh')) {
      const token = getToken();
      if (token) {
        config.headers['token'] = token;
      }
    }

    // ========== 请求防抖：防重复提交（只对写操作防抖，列表查询已白名单放行）==========
    const reqKey = getRequestKey(config);
    if (reqKey) {
      const existing = pendingRequests.get(reqKey);
      // 超时保护：key 超过 5 秒视为过期（防止响应丢失导致永久阻塞）
      if (existing && Date.now() - existing.ts > 5000) {
        console.warn('[request-debounce] 清理过期 key:', reqKey);
        pendingRequests.delete(reqKey);
      }
      // 检查：如果还有效则拦截
      if (pendingRequests.has(reqKey)) {
        console.warn('[request-debounce] 拦截重复请求:', config.method?.toUpperCase(), config.url);
        return Promise.reject({ _debounced: true, message: '重复请求已拦截' });
      }
      pendingRequests.set(reqKey, { ts: Date.now(), url: config.url });
    }

    NProgress.start();
    return config;
  },
  (error) => {
    NProgress.done();
    return Promise.reject(error);
  }
);

// ===== 响应拦截器 =====
apiClient.interceptors.response.use(
  (response) => {
    NProgress.done();
    // 成功 → 立即清理 key，下次同样请求可正常发出
    const reqKey = getRequestKey(response.config);
    if (reqKey) pendingRequests.delete(reqKey);

    const data = response.data;

    // 跳过刷新接口自身的响应处理
    if (response.config.url?.includes('/token/refresh')) {
      return response;
    }

    // access token 过期 → 尝试刷新（600010 "无效的token" 通常是 access token 失效）
    if ((data?.code === '600010' || data?.code === 600010) ||
        (data?.code === '600011' || data?.code === 600011)) {
      return handleTokenExpired(response);
    }

    // 其他鉴权失败 → 直接退出（不走刷新，比如没带 token 的请求被拦截）
    // ★ 但登录接口的业务错误不触发退出，交给登录页自己处理
    const isLoginUrl = response.config.url?.includes('login');
    if (!isLoginUrl && AUTH_FAIL_CODES.has(data?.code)) {
      drainQueue(null);
      clearAuthAndRedirect();
      return Promise.reject(new Error(data?.msg || '登录状态已失效'));
    }

    return response;
  },
  (error) => {
    NProgress.done();

    // 防抖拦截的重复请求 → 返回模拟响应结构，让调用方正常走失败分支
    if (error?._debounced) {
      return Promise.resolve({ data: { code: 429, msg: '请求过于频繁，请勿重复提交', data: null } })
    }
    // 清理（即使失败也要清，否则永远拦）— 只在 error.config 存在时才调用 getRequestKey
    if (error?.config) {
      const reqKey = getRequestKey(error.config);
      if (reqKey) pendingRequests.delete(reqKey);
    }

    // HTTP 401 / 403 → 退出登录（但登录接口本身的失败不触发）
    const isLoginUrl = error?.config?.url?.includes('login');
    if (!isLoginUrl && (error?.response?.status === 401 || error?.response?.status === 403)) {
      drainQueue(null);
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // 网络错误
    Message.error('网络异常，请稍后重试');
    return Promise.reject(error);
  }
);

/**
 * Token 过期处理 — 队列刷新模式
 */
function handleTokenExpired(response) {
  const originalRequest = response.config;

  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject, config: originalRequest });

    if (isRefreshing) {
      return; // 已有刷新在进行，等结果
    }

    isRefreshing = true;

    // 如果连 refreshToken 都没有，直接退出
    const refreshToken = getRefreshToken();
    const userId = getUserId();
    if (!refreshToken || !userId) {
      drainQueue(null);
      clearAuthAndRedirect();
      isRefreshing = false;
      return;
    }

    // 用 apiClient 发刷新请求（不走我们的业务拦截判断，因为 config.url 包含 /token/refresh）
    apiClient.get('token/refresh', {
      headers: { 'refresh-token': refreshToken, 'userId': userId },
    }).then((res) => {
      const rdata = res.data;
      if (rdata?.code == 200 && rdata?.data?.accessToken) {
        // 刷新成功
        setAuth(rdata.data.accessToken, rdata.data.refreshToken);
        drainQueue(rdata.data.accessToken);
      } else {
        // refresh 也失效了（可能返回 600010/600013）
        drainQueue(null);
        clearAuthAndRedirect();
      }
    }).catch((err) => {
      // HTTP 错误或网络错误
      drainQueue(null);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        clearAuthAndRedirect();
      } else {
        // 非鉴权错误（网络超时等），只清队列退出，不重复弹提示
        if (!isLoggingOut) {
          Message.error('刷新登录失败，请重新登录');
          clearAuthAndRedirect();
        }
      }
    }).finally(() => {
      isRefreshing = false;
    });
  });
}

/**
 * 处理队列里的所有等待请求
 */
function drainQueue(newToken) {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (newToken) {
      config.headers['token'] = newToken;
      resolve(apiClient(config));
    } else {
      reject(new Error('登录已失效'));
    }
  });
  failedQueue = [];
}

/**
 * 清除登录态并跳转登录页（全局只弹一次提示）
 */
function clearAuthAndRedirect() {
  if (isLoggingOut) return; // 已在退出流程中，跳过
  isLoggingOut = true;

  if (!tokenExpiredToastShown) {
    tokenExpiredToastShown = true;
    Message.error('您的登录已过期，请重新登录', { duration: 2000 });
    setTimeout(() => { tokenExpiredToastShown = false; isLoggingOut = false; }, 3000);
  } else {
    setTimeout(() => { isLoggingOut = false; }, 3000);
  }

  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user-login');
  router.replace({ path: '/', replace: true });
}

// ===== 对外 request 函数 =====
export default function request(options) {
  if (!options.method) options.method = 'get';
  if (['get', 'delete'].includes(options.method.toLowerCase())) {
    options.params = options.data || options.params;
  }

  return apiClient(options)
    .then((res) => ({ data: res.data }))
    .catch((err) => ({
      data: null,
      meta: {
        status: err?.response?.status || err?.code || 500,
        msg: err?.message || '请求失败',
      },
    }));
}
